import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from './ThemeProvider';
import { menuStructure, ROLE_LEVEL } from '../data/menuStructure';
import NotificationBell from './NotificationBell';
import UserDropdown from './UserDropdown';
import Icon from './Icon';

const TOPBAR_NAV = [
  { id: 'overview', label: 'Overview', categories: ['Overview'], cols: 1 },
  { id: 'operations', label: 'Operations', categories: ['Operations', 'Data & Logs'], cols: 2 },
  { id: 'security', label: 'Security', categories: ['Security'], cols: 2 },
  { id: 'ai', label: 'AI', categories: ['Intelligence'], cols: 1 },
  { id: 'dashboards', label: 'Dashboards', categories: ['Dashboards'], cols: 2 },
  { id: 'system', label: 'System', categories: ['System', 'Monitoring', 'Help & About'], cols: 3 },
];

const OPS_DESC = {
  incidents: 'Triage, acknowledge & resolve',
  workers: 'SSH agent pool health',
  tasks: 'Queued & scheduled jobs',
  executions: 'Command run history',
  automation: 'Self-heal rules & workflows',
  vmware: 'vCenter, ESXi & VMs',
  'domain-checks': 'DNS, SSL & cert expiry',
  services: 'Daemons, processes & exporters',
  monitoring: 'Alert & Prometheus rules',
  ssh: 'Remote terminal sessions',
  sync: 'Wazuh & ClickHouse replication',
  logs: 'Syslog events & audit trail',
  chatops: 'Telegram bot & approvals',
};

function chunk(list, size) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

function TopbarNav({
  currentPage, user, onLogout, onNavClick, onOpenPalette, onMenu, logoUrl,
  incidentCount, workerTotal, execRunningCount, vulnCount, userRole,
}) {
  const { theme, updateTheme } = useTheme();
  const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);
  const userLevel = ROLE_LEVEL[userRole] ?? -1;

  useEffect(() => {
    const close = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenDropdown(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const isAllowed = (item) => {
    if (item.hideIf && item.hideIf(userLevel)) return false;
    return userLevel >= (ROLE_LEVEL[item.minRole] ?? 99);
  };

  const metaFor = (item) => {
    if (item.id === 'incidents') return incidentCount ?? 0;
    if (item.id === 'workers') return workerTotal ?? 0;
    if (item.id === 'vulnerabilities') return vulnCount ?? 0;
    if (item.id === 'executions' && (execRunningCount ?? 0) > 0) return execRunningCount;
    return null;
  };

  const nav = (id) => {
    setOpenDropdown(null);
    onNavClick(id);
  };

  const activeEntryId = TOPBAR_NAV.find((e) =>
    e.categories.some((cat) => {
      const g = menuStructure.find((x) => x.category === cat);
      return g && g.items.some((i) => i.id === currentPage || i.children?.some((c) => c.id === currentPage));
    })
  )?.id;

  const renderItem = (item, indent = false, desc) => (
    <a
      key={item.id}
      className={`dropdown-item${desc ? ' described' : ''}`}
      href={`#${item.id}`}
      style={indent ? { paddingLeft: 34 } : undefined}
      onClick={(e) => { e.preventDefault(); nav(item.id); }}
    >
      <span className="icon"><Icon name={item.icon} size={14} /></span>
      <span className="txt">
        <span className="lbl">{item.label}</span>
        {desc && <span className="desc">{desc}</span>}
      </span>
      {metaFor(item) != null && <span className="meta">{metaFor(item)}</span>}
    </a>
  );

  const renderItems = (items) =>
    items.filter(isAllowed).map((item) => {
      const children = (item.children || []).filter(isAllowed).filter((c) => !c.hideInTopbar);
      return (
        <React.Fragment key={item.id}>
          {item.groupOnly ? null : renderItem(item)}
          {children.map((c) => renderItem(c, true))}
        </React.Fragment>
      );
    });

  const renderColumns = (navEntry) => {
    const groups = navEntry.categories
      .map((cat) => menuStructure.find((g) => g.category === cat))
      .filter(Boolean);

    if (navEntry.cols === 1) {
      return (
        <div className="dropdown">
          {groups.map((g, gi) => (
            <div key={g.category}>
              <div className="dropdown-header">{g.category}</div>
              {renderItems(g.items)}
              {navEntry.dividers && gi < groups.length - 1 && <div className="dropdown-divider" />}
            </div>
          ))}
        </div>
      );
    }

    if (navEntry.id === 'security') {
      const items = groups[0] ? groups[0].items.filter(isAllowed) : [];
      const cols = chunk(items, Math.ceil(items.length / 2));
      return (
        <div className="dropdown mega">
          {cols.map((colItems, i) => (
            <div className="col" key={`col-${i}`}>
              <div className="dropdown-header">{i === 0 ? 'Security' : 'Detection & Response'}</div>
              {colItems.map((item) => (
                <React.Fragment key={item.id}>
                  {renderItem(item)}
                  {item.children?.filter(isAllowed).map((c) => renderItem(c, true))}
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      );
    }

    if (navEntry.id === 'operations') {
      const opsGroup = groups.find((g) => g.category === 'Operations');
      const dataGroup = groups.find((g) => g.category === 'Data & Logs');
      const opsItems = (opsGroup?.items || []).filter(isAllowed);
      const dataItems = (dataGroup?.items || []).filter(isAllowed);
      const infraIds = ['servers', 'devices', 'vmware', 'domain-checks', 'services', 'monitoring', 'ssh', 'sync'];
      const col1 = opsItems.filter((i) => !infraIds.includes(i.id));
      const col2 = opsItems.filter((i) => infraIds.includes(i.id));
      const renderGroup = (items) =>
        items.map((item) => (
          <React.Fragment key={item.id}>
            {renderItem(item, false, OPS_DESC[item.id])}
            {item.children?.filter(isAllowed).map((c) => renderItem(c, true))}
          </React.Fragment>
        ));
      return (
        <div className="dropdown mega described">
          <div className="col">
            <div className="dropdown-header">Operations</div>
            {renderGroup(col1)}
            <div className="dropdown-divider" />
            <div className="dropdown-header">Data &amp; Logs</div>
            {renderGroup(dataItems)}
          </div>
          <div className="col">
            <div className="dropdown-header">Infrastructure &amp; Remote</div>
            {renderGroup(col2)}
          </div>
        </div>
      );
    }

    if (navEntry.id === 'system') {
      return (
        <div className="dropdown mega cols-3">
          {groups.map((g) => (
            <div className="col" key={g.category}>
              <div className="dropdown-header">{g.category}</div>
              {renderItems(g.items)}
            </div>
          ))}
        </div>
      );
    }

    if (navEntry.id === 'dashboards') {
      const dashGroup = groups[0];
      const dashItems = dashGroup ? dashGroup.items.filter(isAllowed) : [];
      const mainGroups = dashItems.filter((i) => i.groupOnly && i.id !== 'dash-grp-svc');
      const svcGroup = dashItems.find((i) => i.id === 'dash-grp-svc');
      const svcChildren = svcGroup ? (svcGroup.children || []).filter(isAllowed) : [];
      const svcCols = chunk(svcChildren, Math.ceil(svcChildren.length / 3));
      return (
        <div className="dropdown mega cols-3">
          {mainGroups.map((item) => (
            <div className="col" key={item.id}>
              <div className="dropdown-header">{item.label}</div>
              {(item.children || []).filter(isAllowed).map((c) => renderItem(c))}
            </div>
          ))}
          {svcGroup && (
            <div className="col full-row" key="svc">
              <div className="dropdown-header">{svcGroup.label}</div>
              <div className="sub-grid">
                {svcCols.map((colItems, i) => (
                  <div className="sub-col" key={`svc-col-${i}`}>
                    {colItems.map((c) => renderItem(c))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    const groupItems = groups[0] ? groups[0].items.filter(isAllowed) : [];
    const cols = chunk(groupItems, Math.ceil(groupItems.length / 3));
    return (
      <div className="dropdown mega cols-3">
        {cols.map((colItems, i) => (
          <div className="col" key={`col-${i}`}>
            {colItems.map((item) => (
              <React.Fragment key={item.id}>
                <div className="dropdown-header">{item.label}</div>
                {item.children?.filter(isAllowed).map((c) => renderItem(c))}
              </React.Fragment>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const cycleTheme = () => {
    const modes = ['light', 'dark', 'system'];
    updateTheme({ mode: modes[(modes.indexOf(theme.mode) + 1) % 3] });
  };

  return (
    <header className="topbar">
      <a className="brand" href="#dashboard" onClick={(e) => { e.preventDefault(); nav('dashboard'); }}>
        <span className="brand-logo">
          <img src={logoUrl || '/synoti-logo.png'} alt="SYNOTI" />
        </span>
        SYNOTI <span className="brand-sub">{process.env.REACT_APP_VERSION || 'dev'}</span>
      </a>

      <nav className="nav" ref={navRef}>
        {TOPBAR_NAV.map((entry) => {
          const hasVisible = entry.categories.some((cat) => {
            const g = menuStructure.find((x) => x.category === cat);
            return g && g.items.some(isAllowed);
          });
          if (!hasVisible) return null;
          const open = openDropdown === entry.id;
          return (
            <div className={`nav-item ${open ? 'open' : ''}`} key={entry.id}>
              <button
                className={`nav-btn ${activeEntryId === entry.id ? 'active' : ''}`}
                onClick={(e) => { e.stopPropagation(); setOpenDropdown(open ? null : entry.id); }}
              >
                {entry.label}
                {entry.id !== 'overview' && (
                  <span className="chevron"><Icon name="chevron-down" size={14} /></span>
                )}
              </button>
              {open && renderColumns(entry)}
            </div>
          );
        })}
      </nav>

      <div className="topbar-right">
        <button className="icon-btn" id="sidebarMenuBtn" title="Menu" onClick={onMenu}>
          <Icon name="menu" size={18} />
        </button>
        <div className="search-box">
          <input
            type="text"
            placeholder="Search pages…"
            autoComplete="off"
            readOnly
            onMouseDown={(e) => { e.preventDefault(); onOpenPalette(); }}
            onClick={() => onOpenPalette()}
          />
          <kbd>⌘K</kbd>
        </div>
        <button className="icon-btn" title="Toggle theme" onClick={cycleTheme}>
          <Icon name={theme.mode === 'light' ? 'sun' : 'moon'} size={18} />
        </button>
        <NotificationBell />
        <UserDropdown user={user} onLogout={onLogout} />
      </div>
    </header>
  );
}

export default TopbarNav;
